import { getToken, removeToken, setToken } from '@/utils/auth';
import { clearOrgUiConfigCache } from '@/utils/orgUiConfigCache';
import { STORAGE_KEYS } from '@/constants/app';
import {
    ACCESS_TOKEN_ACTIVITY_TTL_MS,
    isSameAuthenticatedUser,
    REFRESH_LOCK_TTL_MS,
    REFRESH_WAIT_TIMEOUT_MS
} from '@/utils/authSession.mjs';

const CHANNEL_NAME = 'automachine-admin-auth';
const EVENT_KEY = 'automachine_admin_auth_event';
const REFRESH_LOCK_KEY = 'automachine_admin_refresh_lock';
const REFRESH_LOCK_TTL = REFRESH_LOCK_TTL_MS;
const REFRESH_WAIT_TIMEOUT = REFRESH_WAIT_TIMEOUT_MS;

const MESSAGE_TYPE = {
    TOKEN: 'token',
    ACCOUNT_CHANGED: 'account-changed',
    LOGOUT: 'logout',
    REFRESH_STARTED: 'refresh-started',
    REFRESH_FAILED: 'refresh-failed',
};

const tabId = createTabId();
const tokenListeners = [];
const logoutListeners = [];
const refreshFailedListeners = [];

let channel = null;

function hasWindow() {
    return typeof window !== 'undefined';
}

function createTabId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function canUseStorage() {
    if (!hasWindow()) {
        return false;
    }
    try {
        return !!window.localStorage;
    } catch (e) {
        return false;
    }
}

function canUseBroadcastChannel() {
    return hasWindow() && typeof window.BroadcastChannel === 'function';
}

function getChannel() {
    if (!canUseBroadcastChannel()) {
        return null;
    }
    if (!channel) {
        channel = new window.BroadcastChannel(CHANNEL_NAME);
        channel.onmessage = (event) => {
            handleIncomingMessage(event.data);
        };
    }
    return channel;
}

function postStorageEvent(message) {
    if (!canUseStorage() || message.type === MESSAGE_TYPE.TOKEN) {
        return;
    }
    try {
        const payload = JSON.stringify({
            ...message,
            createdAt: Date.now(),
        });
        window.localStorage.setItem(EVENT_KEY, payload);
        window.localStorage.removeItem(EVENT_KEY);
    } catch (e) {}
}

function postMessage(message) {
    const payload = {
        ...message,
        source: tabId,
    };
    const activeChannel = getChannel();
    if (activeChannel) {
        activeChannel.postMessage(payload);
    }
    postStorageEvent(payload);
}

function notify(listeners, payload) {
    listeners.slice().forEach((listener) => {
        try {
            listener(payload);
        } catch (e) {}
    });
}

function clearReplenisherStockOnlySession() {
    if (!canUseStorage()) {
        return;
    }
    try {
        window.localStorage.removeItem('replenisher_stock_only');
        window.localStorage.removeItem('authid');
    } catch (e) {}
}

function clearHeaderSession() {
    if (!hasWindow()) {
        return;
    }
    try {
        window.sessionStorage.removeItem(STORAGE_KEYS.sessionFilesUrl);
        window.sessionStorage.removeItem(STORAGE_KEYS.sessionUsername);
        window.sessionStorage.removeItem(STORAGE_KEYS.sessionHeadimgurl);
        window.sessionStorage.removeItem(STORAGE_KEYS.sessionUserid);
        window.sessionStorage.removeItem(STORAGE_KEYS.redirectPath);
    } catch (e) {}
}

function isSameAuthUser(userId) {
    if (!hasWindow() || userId == null || userId === '') {
        return false;
    }
    try {
        const currentUserId = window.sessionStorage.getItem(STORAGE_KEYS.sessionUserid);
        return isSameAuthenticatedUser(currentUserId, userId);
    } catch (e) {
        return false;
    }
}

function handleAccountChanged() {
    removeToken();
    clearHeaderSession();
    clearOrgUiConfigCache();
    notify(logoutListeners, { reason: MESSAGE_TYPE.ACCOUNT_CHANGED });
}

function handleIncomingMessage(message) {
    if (!message || message.source === tabId) {
        return;
    }

    if (message.type === MESSAGE_TYPE.TOKEN && message.token) {
        if (!isSameAuthUser(message.userId)) {
            handleAccountChanged();
            return;
        }
        setToken(message.token, ACCESS_TOKEN_ACTIVITY_TTL_MS);
        notify(tokenListeners, message.token);
        return;
    }

    if (message.type === MESSAGE_TYPE.ACCOUNT_CHANGED) {
        handleAccountChanged();
        return;
    }

    if (message.type === MESSAGE_TYPE.LOGOUT) {
        removeToken();
        clearHeaderSession();
        clearOrgUiConfigCache();
        clearReplenisherStockOnlySession();
        notify(logoutListeners, { reason: MESSAGE_TYPE.LOGOUT });
        return;
    }

    if (message.type === MESSAGE_TYPE.REFRESH_FAILED) {
        notify(refreshFailedListeners);
    }
}

function setupStorageListener() {
    if (!hasWindow()) {
        return;
    }
    window.addEventListener('storage', (event) => {
        if (event.key !== EVENT_KEY || !event.newValue) {
            return;
        }
        try {
            handleIncomingMessage(JSON.parse(event.newValue));
        } catch (e) {}
    });
}

function readRefreshLock() {
    if (!canUseStorage()) {
        return null;
    }
    try {
        const raw = window.localStorage.getItem(REFRESH_LOCK_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
}

function acquireRefreshLock() {
    if (!canUseStorage()) {
        return true;
    }

    const now = Date.now();
    const current = readRefreshLock();
    if (current && current.owner !== tabId && Number(current.expiresAt) > now) {
        return false;
    }

    const next = {
        owner: tabId,
        expiresAt: now + REFRESH_LOCK_TTL,
    };

    try {
        window.localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(next));
        const saved = readRefreshLock();
        return saved && saved.owner === tabId;
    } catch (e) {
        return true;
    }
}

function releaseRefreshLock() {
    if (!canUseStorage()) {
        return;
    }
    const current = readRefreshLock();
    if (!current || current.owner !== tabId) {
        return;
    }
    try {
        window.localStorage.removeItem(REFRESH_LOCK_KEY);
    } catch (e) {}
}

function waitForSyncedToken(timeout = REFRESH_WAIT_TIMEOUT) {
    return new Promise((resolve, reject) => {
        let done = false;
        let timer = null;

        const cleanup = () => {
            done = true;
            clearTimeout(timer);
            removeListener(tokenListeners, onToken);
            removeListener(refreshFailedListeners, onFailed);
            removeListener(logoutListeners, onLogout);
        };
        const onToken = (token) => {
            if (done) {
                return;
            }
            cleanup();
            resolve({ token });
        };
        const onFailed = () => {
            if (done) {
                return;
            }
            cleanup();
            reject(new Error('silent refresh failed in another tab'));
        };
        const onLogout = () => {
            if (done) {
                return;
            }
            const error = new Error('logged out in another tab');
            error.authLogout = true;
            cleanup();
            reject(error);
        };

        tokenListeners.push(onToken);
        refreshFailedListeners.push(onFailed);
        logoutListeners.push(onLogout);
        timer = setTimeout(() => {
            if (done) {
                return;
            }
            cleanup();
            reject(new Error('silent refresh wait timeout'));
        }, timeout);
    });
}

function removeListener(listeners, listener) {
    const index = listeners.indexOf(listener);
    if (index >= 0) {
        listeners.splice(index, 1);
    }
}

function runOwnedSilentLogin(refreshFn) {
    postMessage({ type: MESSAGE_TYPE.REFRESH_STARTED });
    return Promise.resolve()
        .then(refreshFn)
        .then((data) => {
            if (data && data.token) {
                broadcastAuthToken(data.token, data?.info?.userid);
            }
            return data;
        })
        .catch((error) => {
            // Let another waiting tab acquire the lock before it receives failure.
            releaseRefreshLock();
            postMessage({ type: MESSAGE_TYPE.REFRESH_FAILED });
            throw error;
        })
        .finally(() => {
            releaseRefreshLock();
        });
}

export function broadcastAuthToken(token, userId) {
    if (!token) {
        return;
    }
    postMessage({
        type: MESSAGE_TYPE.TOKEN,
        token,
        userId,
    });
}

export function setSyncedToken(token, userId) {
    if (!token) {
        return;
    }
    setToken(token, ACCESS_TOKEN_ACTIVITY_TTL_MS);
    broadcastAuthToken(token, userId);
}

export function setPasswordLoginToken(token, userId) {
    if (!token) {
        return;
    }
    try {
        if (userId == null || userId === '') {
            window.sessionStorage.removeItem(STORAGE_KEYS.sessionUserid);
        } else {
            window.sessionStorage.setItem(STORAGE_KEYS.sessionUserid, String(userId));
        }
    } catch (e) {}
    setToken(token);
    postMessage({
        type: MESSAGE_TYPE.ACCOUNT_CHANGED,
        userId,
    });
}

export function broadcastLogout() {
    clearReplenisherStockOnlySession();
    postMessage({ type: MESSAGE_TYPE.LOGOUT });
}

export function onAuthLogout(listener) {
    if (typeof listener !== 'function') {
        return () => {};
    }
    logoutListeners.push(listener);
    return () => removeListener(logoutListeners, listener);
}

export function runExclusiveSilentLogin(refreshFn) {
    if (acquireRefreshLock()) {
        return runOwnedSilentLogin(refreshFn);
    }

    return waitForSyncedToken()
        .then((data) => {
            if (data && data.token && getToken() !== data.token) {
                setToken(data.token, ACCESS_TOKEN_ACTIVITY_TTL_MS);
            }
            return data;
        })
        .catch((error) => {
            if (error && error.authLogout) {
                return Promise.reject(error);
            }
            if (acquireRefreshLock()) {
                return runOwnedSilentLogin(refreshFn);
            }
            return waitForSyncedToken();
        });
}

if (canUseBroadcastChannel()) {
    getChannel();
}
setupStorageListener();
